import type { UserEditSchema } from '@repo/schemas/user/userEdit';
import type { GroupPublic } from '@repo/types/groups';
import type { UserPrivate, UserPublic } from '@repo/types/users';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useEditGroup } from '@repo/api/group/useEditGroup';
import { useGetGroupsInfinite } from '@repo/api/group/useGetGroupsInfinite';
import { useGetParentChildren } from '@repo/api/parent/useGetParentChildren';
import { useLinkParent } from '@repo/api/parent/useLinkParent';
import { useUnlinkParent } from '@repo/api/parent/useUnlinkParent';
import { useEditUser } from '@repo/api/user/useEditUser';
import { useGetUser } from '@repo/api/user/useGetUser';
import { useGetUserGroups } from '@repo/api/user/useGetUserGroups';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { userErrorMessages } from '@repo/lib/messages/userErrorMessages';
import { countStudents } from '@repo/lib/user-lvl/countStudents';
import { userEditSchema, userEditSchemaParser } from '@repo/schemas/user/userEdit';
import { AccountType } from '@repo/types/enums/accountType';
import { UserLvl } from '@repo/types/enums/userLvl';
import { CheckCircle2Icon, ChevronLeftIcon, ChevronsUpDownIcon, PlusIcon, UserIcon, UsersIcon, XIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EmptyState, EmptyStateContent, EmptyStateDescription, EmptyStateFooter, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Form } from '@/components/ui/form';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { SearchInput } from '@/components/ui/input';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  SelectList,
  SelectListElement,
  SelectListElementContent,
  SelectListElementDescription,
  SelectListElementSkeleton,
  SelectListElementTitle,
  SelectListElementTrigger,
  SelectListInfiniteQueryStates,
} from '@/components/ui/select-list';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Tag } from '@/components/ui/tags';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { getAvatar } from '@/utils/getAvatar/getAvatar';

import { EditUserParentSection } from './EditUserParentSection';
import { UserFormFields } from './UserFormFields';

/**
 * Props for the EditUserDialog component.
 * @property user - The user to edit.
 * @property open - Whether the dialog is open.
 * @property onOpenChange - Callback to change dialog open state.
 * @property accountType - Account type the user being edited has. Defaults to standard user; pass `AccountType.Parent` to swap the groups panel for a linked-students panel and hide the lvl field.
 * @property students - When editing a parent, the list of students available for linking.
 * @property parents - When editing a student in user mode, the list of parents available for linking.
 */
interface EditUserDialogProps {
  user?: UserPrivate;
  open: boolean;
  onOpenChange: (value: boolean) => void;
  accountType?: AccountType;
  students?: UserPrivate[];
  parents?: UserPrivate[];
}

interface EditUserTarget {
  user: UserPublic | UserPrivate;
  accountType: AccountType;
}

export default function EditUserDialog(editUserProps: EditUserDialogProps) {
  const { user, open, onOpenChange, accountType = AccountType.User, students = [], parents = [] } = editUserProps;

  const [navStack, setNavStack] = useState<EditUserTarget[]>([]);
  const baseTarget: EditUserTarget | undefined = user ? { user, accountType } : undefined;
  const currentTarget = navStack.at(-1) ?? baseTarget;
  const currentUser = currentTarget?.user;
  const isParent = currentTarget?.accountType === AccountType.Parent;
  const canGoBack = navStack.length > 0;

  const [isPending, setIsPending] = useState<boolean>(false);
  const [tabs, setTabs] = useState<'main' | 'selectedList'>('main');

  const [searchUserValue, setSearchUserValue] = useState<string>('');
  const searchGroupDebouncedValue = useDebounceValue(searchUserValue, 400);
  const [selectedGroups, setSelectedGroups] = useState<GroupPublic[]>([]);
  const [studentPickerOpen, setStudentPickerOpen] = useState(false);
  const { data: userData, ...userResults } = useGetUser(currentUser?.id);
  const { data: userGroups = [], ...userGroupsResults } = useGetUserGroups(currentUser?.id);

  const editedUser = userData ?? currentUser;
  const isStudent = !isParent && editedUser?.lvl === UserLvl.STUDENT && editedUser?.accountType !== AccountType.Parent;

  const { data: linkedChildren = [], isFetching: isChildrenFetching } = useGetParentChildren(isParent ? currentUser?.id : undefined);
  const { mutateAsync: linkParent } = useLinkParent();
  const { mutateAsync: unlinkParent } = useUnlinkParent();

  const linkedChildIds = useMemo(() => new Set(linkedChildren.map((child: UserPublic) => child.id)), [linkedChildren]);
  const availableStudents = useMemo(() => students.filter((student) => student.id !== currentUser?.id && !linkedChildIds.has(student.id)), [students, linkedChildIds, currentUser?.id]);

  const handleLinkChild = (student: UserPrivate) => {
    if (!currentUser) return;
    setStudentPickerOpen(false);
    linkParent({ parentId: currentUser.id, studentId: student.id }).catch((error) => {
      toasterMessageHandler(baseErrorMessages, error);
    });
  };

  const handleUnlinkChild = (childId: number) => {
    if (!currentUser) return;
    unlinkParent({ parentId: currentUser.id, studentId: childId }).catch((error) => {
      toasterMessageHandler(baseErrorMessages, error);
    });
  };

  const resetTransientState = () => {
    setSelectedGroups([]);
    setTabs('main');
    setStudentPickerOpen(false);
  };

  const navigateToTarget = (target: UserPublic, targetAccountType: AccountType) => {
    resetTransientState();
    setNavStack((stack) => [...stack, { user: target, accountType: targetAccountType }]);
  };

  const navigateBack = () => {
    resetTransientState();
    setNavStack((stack) => stack.slice(0, -1));
  };

  const isMainTab = tabs === 'main';

  const { errorMessage: groupsErrorMessage, ...getGroupsInfinite } = useGetGroupsInfinite(searchGroupDebouncedValue);

  const { data: groups, isEmpty: groupsIsEmpty, noResults: groupsNoResults } = useInfiniteQueryResult(getGroupsInfinite.data, searchGroupDebouncedValue);

  const { mutateAsync: userEdit } = useEditUser();
  const { mutate: groupEdit } = useEditGroup();

  const form = useForm<UserEditSchema>({
    mode: 'onBlur',
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      lvl: 0,
      phone: null,
      birthdate: null,
      active: true,
      force_password_change: undefined,
    },
    resolver: standardSchemaResolver(userEditSchema),
  });

  const onRemoveGroup = (id: number) => {
    setSelectedGroups((prev) => prev.filter((g) => g.id !== id));
  };

  const onTabsValueChange = (tab: string) => {
    setTabs(tab as 'main' | 'selectedList');
  };

  const onSubmit = async (data: UserEditSchema) => {
    setIsPending(true);

    if (!isParent) {
      const groupsToAdd = selectedGroups.filter((selectedGroup) => userGroups.every((userGroup) => userGroup.id !== selectedGroup.id));
      const groupsToRemove = userGroups.filter((userGroup) => !selectedGroups.find((selectedGroup) => selectedGroup.id === userGroup.id));

      groupsToAdd.forEach(({ id }) => {
        groupEdit({
          id: id,
          memberChanges: [
            {
              userIds: currentUser?.id ? [currentUser.id] : [],
              add: true,
            },
          ],
        });
      });
      groupsToRemove.forEach(({ id }) => {
        groupEdit({
          id: id,
          memberChanges: [
            {
              userIds: currentUser?.id ? [currentUser.id] : [],
              remove: true,
            },
          ],
        });
      });
    }

    await userEdit(data)
      .then(() => {
        if (canGoBack) {
          navigateBack();
        } else {
          onClose();
        }
      })
      .catch((error) => {
        toasterMessageHandler(userErrorMessages, error);
      });

    setIsPending(false);
  };

  const onClose = () => {
    onOpenChange(false);
    setSearchUserValue('');
    setSelectedGroups([]);
    setIsPending(false);
    setTabs('main');
    setStudentPickerOpen(false);
    setNavStack([]);
    form.reset();
  };

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      onClose();
    }
  };

  useEffect(() => {
    if (!isParent && userGroupsResults.isSuccess && open) {
      setSelectedGroups(userGroups);
    }
  }, [isParent, userGroupsResults.isSuccess, open, userGroups]);

  useEffect(() => {
    if (userResults.isError) {
      toasterMessageHandler(baseErrorMessages, userResults.error);
      onClose();
    } else if (!isParent && userGroupsResults.isError) {
      toasterMessageHandler(baseErrorMessages, userGroupsResults.error);
      onClose();
    }
  }, [isParent, userResults.isError, userGroupsResults.isError, userGroupsResults.error, userResults.error, onClose]);

  useEffect(() => {
    if (userData) {
      const parsed = userEditSchemaParser.safeParse(userData);
      if (parsed.success) {
        form.reset(parsed.data);
      }
    }
  }, [userData, form]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent isClosable={isMainTab} className={'sm:h-[85dvh] sm:min-h-96 sm:max-w-108'} onCloseAutoFocus={onClose} onEscapeKeyDown={onClose}>
        <DialogDescription className={'sr-only'}>Formularz do edycji użytkownika</DialogDescription>
        <Form {...form}>
          <DialogBody>
            <LoadingOverlay isLoading={isPending}>
              <LoadingOverlaySpinner />
              <LoadingOverlayLabel>Trwa zapisywanie zmian...</LoadingOverlayLabel>
            </LoadingOverlay>
            <LoadingOverlay isLoading={userResults.isFetching || (isParent ? isChildrenFetching : userGroupsResults.isFetching)}>
              <LoadingOverlaySpinner />
              <LoadingOverlayLabel>Trwa ładowanie danych...</LoadingOverlayLabel>
            </LoadingOverlay>
            {isMainTab ? (
              <DialogHeader className={'border-border-primary intems-center bg-card border-b'}>
                {canGoBack && (
                  <Button type={'button'} variant={'outline'} size={'sm'} onClick={navigateBack} aria-label={'Wróć'}>
                    <ChevronLeftIcon />
                  </Button>
                )}
                <FeaturedIcon size={'lg'}>
                  <UserIcon />
                </FeaturedIcon>
                <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                  <DialogTitle>{isParent ? 'Edycja Rodzica' : 'Edycja Użytkownika'}</DialogTitle>
                  <DialogDescription className={'text-muted-foreground text-xs'}>
                    {isParent ? 'Edytuj dane konta rodzica i powiązanych uczniów.' : 'Wprowadź wymagane dane użytkownika.'}
                  </DialogDescription>
                </div>
              </DialogHeader>
            ) : (
              <DialogHeader>
                <DialogTitle className={'sr-only'}>Edytuj Grupę</DialogTitle>
                <Button variant={'outline'} onClick={() => setTabs('main')}>
                  <ChevronLeftIcon />
                </Button>
                <SearchInput setValue={setSearchUserValue} />
              </DialogHeader>
            )}
            <Tabs value={tabs} onValueChange={onTabsValueChange} className={'flex grow flex-col gap-3 overflow-auto p-4'}>
              <TabsContent value={'main'}>
                <form className={'flex grow flex-col gap-3'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
                  <UserFormFields hideLvl={isParent} />
                </form>
                {isParent ? (
                  <div className={'space-y-3 pt-3'}>
                    <h5 className={'font-medium'}>Powiązani uczniowie:</h5>
                    <div className={'flex flex-col gap-2'}>
                      {linkedChildren.length === 0 ? (
                        <EmptyState className={'border-border-secondary bg-muted/25 w-full rounded-md border pt-4!'}>
                          <EmptyStateContent>
                            <EmptyStateTitle className={'text-sm!'}>Brak powiązanych uczniów</EmptyStateTitle>
                            <EmptyStateDescription className={'max-w-xs text-xs!'}>Powiąż ucznia z tym rodzicem.</EmptyStateDescription>
                          </EmptyStateContent>
                        </EmptyState>
                      ) : (
                        <ul className={'flex flex-col gap-2'}>
                          {linkedChildren.map((child: UserPublic) => (
                            <li key={child.id} className={'border-border-primary bg-card/75 flex items-center gap-3 rounded-md border p-2'}>
                              <button
                                type={'button'}
                                onClick={() => navigateToTarget(child, AccountType.User)}
                                className={'hover:bg-muted/40 -m-1 flex min-w-0 grow items-center gap-3 rounded-md p-1 text-left transition-colors'}
                                aria-label={`Otwórz ucznia ${child.name} ${child.surname}`}
                              >
                                <Avatar size={'sm'}>
                                  <AvatarImage src={child.avatarURL ? getAvatar(child.avatarURL) : undefined} />
                                  <AvatarFallback>
                                    <UserIcon className={'size-3.5'} />
                                  </AvatarFallback>
                                </Avatar>
                                <div className={'flex min-w-0 grow flex-col'}>
                                  <p className={'truncate text-sm'}>
                                    {child.name} {child.surname}
                                  </p>
                                  <p className={'text-text-tertiary truncate text-xs'}>{child.email}</p>
                                </div>
                              </button>
                              <Button
                                size={'sm'}
                                variant={'ghost'}
                                variantColor={'destructive'}
                                onClick={() => handleUnlinkChild(child.id)}
                                aria-label={`Odepnij ucznia ${child.name} ${child.surname}`}
                              >
                                <XIcon />
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <Popover open={studentPickerOpen} onOpenChange={setStudentPickerOpen}>
                        <PopoverTrigger asChild>
                          <Button type={'button'} variant={'outline'} role={'combobox'} className={'w-full justify-between'}>
                            <span className={'flex items-center gap-2'}>
                              <PlusIcon className={'size-4'} />
                              Powiąż ucznia
                            </span>
                            <ChevronsUpDownIcon className={'text-muted-foreground'} />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className={'min-w-xs p-0'} side={'top'} align={'start'} disablePortal>
                          <Command>
                            <CommandInput placeholder={'Szukaj ucznia...'} />
                            <CommandList>
                              <CommandEmpty>{availableStudents.length === 0 ? 'Brak dostępnych uczniów.' : 'Nie znaleziono ucznia.'}</CommandEmpty>
                              <CommandGroup>
                                {availableStudents.map((student) => (
                                  <CommandItem key={student.id} value={`${student.name} ${student.surname} ${student.email}`} onSelect={() => handleLinkChild(student)} className="h-min">
                                    <Avatar size={'sm'}>
                                      <AvatarImage src={student.avatarURL ? getAvatar(student.avatarURL) : undefined} />
                                      <AvatarFallback>
                                        <UserIcon className={'size-3.5'} />
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className={'flex min-w-0 flex-col'}>
                                      <p className={'truncate text-sm'}>
                                        {student.name} {student.surname}
                                      </p>
                                      <p className={'text-text-tertiary truncate text-xs'}>{student.email}</p>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                ) : (
                  <div className={'space-y-3'}>
                    <div>
                      <h5 className={'font-medium'}>Grupy:</h5>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {selectedGroups.map(({ id, name, avatarURL, slots, users = [] }) => {
                        const studentsCount = countStudents(users);
                        const isOverLimit: boolean = typeof slots === 'number' && studentsCount > slots;
                        const tag = (
                          <Tag key={id} onRemove={() => onRemoveGroup(id)} variant={isOverLimit ? 'warning' : 'muted'} border>
                            <Avatar size={'xs'}>
                              <AvatarImage src={getAvatar(avatarURL)} alt={name} />
                              <AvatarFallback>
                                <UsersIcon />
                              </AvatarFallback>
                            </Avatar>
                            {name}{' '}
                            {isOverLimit && (
                              <span className="inline-flex items-center gap-0.5 text-xs">
                                ({studentsCount}/{slots})
                              </span>
                            )}
                          </Tag>
                        );

                        return isOverLimit ? (
                          <Tooltip key={id}>
                            <TooltipTrigger asChild>
                              <span className="inline-block">{tag}</span>
                            </TooltipTrigger>
                            <TooltipContent>Liczba użytkowników przekracza limit miejsc w grupie</TooltipContent>
                          </Tooltip>
                        ) : (
                          tag
                        );
                      })}
                      {selectedGroups.length > 0 && (
                        <Badge variant="muted" border className="cursor-pointer" onClick={() => onTabsValueChange('selectedList')} asChild>
                          <button type="button">
                            <PlusIcon />
                            Dodaj
                          </button>
                        </Badge>
                      )}
                      {selectedGroups.length === 0 && (
                        <EmptyState className="border-border-secondary bg-muted/25 w-full rounded-md border pt-4!">
                          <EmptyStateContent>
                            <EmptyStateTitle className="text-sm!">Brak wybranych grup</EmptyStateTitle>
                            <EmptyStateDescription className="max-w-xs text-xs!">Wybierz grupy aby przypisać je do użytkownika</EmptyStateDescription>
                            <EmptyStateFooter>
                              <Button type={'button'} variant={'outline'} size="sm" onClick={() => onTabsValueChange('selectedList')}>
                                <PlusIcon />
                                Dodaj grupy
                              </Button>
                            </EmptyStateFooter>
                          </EmptyStateContent>
                        </EmptyState>
                      )}
                    </div>
                    {isStudent && currentUser && (
                      <EditUserParentSection key={currentUser.id} studentId={currentUser.id} parents={parents} onOpenParent={(parent) => navigateToTarget(parent, AccountType.Parent)} />
                    )}
                  </div>
                )}
              </TabsContent>
              <TabsContent value={'selectedList'}>
                <SelectList
                  type={'multiple'}
                  selected={selectedGroups.map((g) => String(g.id))}
                  setSelected={(ids) => {
                    const selectedIds = new Set(ids.map(Number));
                    const updatedGroups = [...selectedGroups.filter((g) => selectedIds.has(g.id)), ...groups.filter((g) => selectedIds.has(g.id) && !selectedGroups.some((s) => s.id === g.id))];
                    setSelectedGroups(updatedGroups);
                  }}
                >
                  <SelectListInfiniteQueryStates
                    query={getGroupsInfinite}
                    isEmpty={groupsIsEmpty}
                    noResults={groupsNoResults}
                    isEmptyMessage={'Wyszukaj grupę'}
                    noResultMessage={'Nie znaleziono grupy'}
                    errorMessage={groupsErrorMessage}
                  />

                  {groups.map((group) => (
                    <SelectListElement key={group.id} id={String(group.id)}>
                      <Avatar size={'sm'}>
                        <AvatarImage src={getAvatar(group.avatarURL)} alt={group.name} />
                        <AvatarFallback>{group.name?.[0] || <UsersIcon />}</AvatarFallback>
                      </Avatar>
                      <SelectListElementContent>
                        <SelectListElementTitle>{group.name}</SelectListElementTitle>
                        <SelectListElementDescription>
                          {group.slots !== undefined ? (
                            <span className="inline-flex items-center gap-1">
                              Liczba miejsc: {countStudents(group.users ?? [])} / {group.slots}
                            </span>
                          ) : (
                            '-'
                          )}
                        </SelectListElementDescription>
                      </SelectListElementContent>

                      <SelectListElementTrigger>
                        {({ selected, toggleElement }) => (
                          <Checkbox
                            checked={selected}
                            onCheckedChange={() => {
                              toggleElement();
                            }}
                            className="after:absolute after:inset-0"
                          />
                        )}
                      </SelectListElementTrigger>
                    </SelectListElement>
                  ))}
                  <InfiniteScroll
                    hasNextPage={getGroupsInfinite.hasNextPage}
                    isLoading={getGroupsInfinite.isLoading}
                    isFetching={getGroupsInfinite.isFetching}
                    next={getGroupsInfinite.fetchNextPage}
                    threshold={0.65}
                  >
                    {getGroupsInfinite.hasNextPage && <SelectListElementSkeleton />}
                  </InfiniteScroll>
                </SelectList>
              </TabsContent>
            </Tabs>
          </DialogBody>
          {isMainTab ? (
            <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
              <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
                Anuluj
              </Button>
              <Button disabled={isPending} onClick={form.handleSubmit(onSubmit)} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
                <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                  <CheckCircle2Icon />
                  Zapisz
                </LoadingSwap>
              </Button>
            </DialogFooter>
          ) : (
            <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
              <Button onClick={() => setTabs('main')} type={'button'} variant={'tertiary'}>
                Zapisz
              </Button>
            </DialogFooter>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  );
}
